import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  input,
  output,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatSliderModule } from '@angular/material/slider';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ModusButtonModule, ModusIconModule } from '@trimble-gcs/modus';
import { map } from 'rxjs';
import { ColorPickerComponent } from '../../../color-picker/color-picker.component';
import { ColorPickerFormDirective } from '../../../color-picker/color-picker.directive';
import { Scan3dStyle } from '../../models/scan-3d-style';
import { scan3dIntensityTranslations } from './scan-3d-intensity.translations';

@UntilDestroy()
@Component({
  selector: 'sd-scan-3d-intensity',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ModusButtonModule,
    ModusIconModule,
    MatSliderModule,
    ColorPickerComponent,
    ColorPickerFormDirective,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './scan-3d-intensity.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Scan3dIntensityComponent {
  readonly scan3dStyle = input.required<Scan3dStyle>();

  readonly styleChange = output<Partial<Scan3dStyle>>();

  readonly formGroup = new FormGroup({
    offset: new FormControl<number>(0.5, { nonNullable: true }),
    startColor: new FormControl<string>('#000000', { nonNullable: true }),
    endColor: new FormControl<string>('#ffffff', { nonNullable: true }),
  });

  readonly translations = scan3dIntensityTranslations();

  constructor() {
    this.subscribeToScan3dStyle();
    this.subscribeToFormValueChanges();
  }

  private subscribeToScan3dStyle() {
    toObservable(this.scan3dStyle)
      .pipe(untilDestroyed(this))
      .subscribe((style) => {
        this.formGroup.patchValue(
          {
            offset: style.intensityOffset,
            startColor: style.intensityStartColor,
            endColor: style.intensityEndColor,
          },
          { emitEvent: false },
        );
      });
  }

  private subscribeToFormValueChanges() {
    this.formGroup.valueChanges
      .pipe(
        map(
          (changes) =>
            ({
              intensityOffset: changes.offset ?? 0.5,
              intensityStartColor: changes.startColor ?? '#000000',
              intensityEndColor: changes.endColor ?? '#ffffff',
            }) satisfies Partial<Scan3dStyle>,
        ),
        untilDestroyed(this),
      )
      .subscribe((partialStyle) => this.styleChange.emit(partialStyle));
  }
}
